import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3, TrendingUp, TrendingDown, Users, MapPin, Smartphone,
  Target, Shield, AlertTriangle, Activity, Download, RefreshCw,
  Database,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════
// Analytics Service
// Calls GET /api/analytics/business (served by analytics.js)
// No mock/fallback data — shows empty states when API fails.
// ═══════════════════════════════════════════════════════════
class AnalyticsService {
  private baseURL: string;

  constructor() {
    const envUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
      || (typeof process !== 'undefined' && process.env?.VITE_API_URL)
      || '';
    if (envUrl) {
      this.baseURL = envUrl;
    } else {
      const host = window.location.hostname;
      if (host === 'jeptags.com' || host === 'www.jeptags.com' || host.endsWith('.jeptags.com')) {
        this.baseURL = 'https://api.jeptags.com/api';
      } else {
        this.baseURL = '/api';
      }
    }
  }

  private getToken() {
    return localStorage.getItem('businessToken')
      || localStorage.getItem('auth_token')
      || localStorage.getItem('authToken')
      || localStorage.getItem('token')
      || '';
  }

  private authHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getToken()}`
    };
  }

  private getDateRange(range: string): { startDate: string; endDate: string } {
    const end = new Date(), start = new Date();
    switch (range) {
      case '24h': start.setHours(start.getHours() - 24); break;
      case '7d':  start.setDate(start.getDate() - 7); break;
      case '30d': start.setDate(start.getDate() - 30); break;
      case '90d': start.setDate(start.getDate() - 90); break;
      default:    start.setDate(start.getDate() - 7);
    }
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  async getAnalytics(timeRange: string = '7d'): Promise<{ success: boolean; error?: string; data: AnalyticsData | null }> {
    const { startDate, endDate } = this.getDateRange(timeRange);
    const granularity = timeRange === '24h' ? 'hour' : timeRange === '90d' ? 'month' : 'day';
    const qs = `startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`;

    try {
      const res = await fetch(`${this.baseURL}/analytics/business?${qs}`, {
        headers: this.authHeaders()
      });

      if (res.status === 401 || res.status === 403) {
        return { success: false, error: 'Authentication required. Please log in again.', data: null };
      }

      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('application/json')) {
        return { success: false, error: `Server returned ${res.status}. Please check your API configuration.`, data: null };
      }

      const json = await res.json();
      if (json.success && json.data) {
        return { success: true, data: this.normalize(json.data) };
      }

      return { success: false, error: json.message || 'Unexpected API response', data: null };
    } catch (err) {
      console.error('Analytics fetch error:', err);
      return { success: false, error: 'Unable to connect to analytics service. Please check your network.', data: null };
    }
  }

  private normalize(raw: any): AnalyticsData {
    return {
      overview: raw.overview || { totalScans: 0, scanGrowth: 0, uniqueUsers: 0, userGrowth: 0, conversionRate: 0, conversionGrowth: 0, dailyAverage: 0, dailyGrowth: 0, revenue: 0, revenueGrowth: 0 },
      scanTrends: raw.scanTrends || raw.trends || [],
      locationData: raw.locationData || raw.locations || [],
      deviceBreakdown: raw.deviceBreakdown || raw.devices || [],
      topProducts: raw.topProducts || [],
      hourlyPattern: raw.hourlyPattern || [],
      securityMetrics: raw.securityMetrics || { fraudAlerts: 0, blockedScans: 0, suspiciousActivity: 0, verificationRate: 0 }
    };
  }
}

// ═══════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════
interface AnalyticsOverview {
  totalScans: number; scanGrowth: number;
  uniqueUsers: number; userGrowth: number;
  conversionRate: number; conversionGrowth: number;
  dailyAverage: number; dailyGrowth: number;
  revenue: number; revenueGrowth: number;
}

interface AnalyticsData {
  overview: AnalyticsOverview;
  scanTrends: Array<{ date: string; scans: number; users: number; conversions: number; revenue: number }>;
  locationData: Array<{ city: string; scans: number; growth: number; percentage: number }>;
  deviceBreakdown: Array<{ device: string; scans: number; percentage: number; growth: number }>;
  topProducts: Array<{ name: string; scans: number; revenue: number; growth: number }>;
  hourlyPattern: Array<{ hour: string; scans: number }>;
  securityMetrics: { fraudAlerts: number; blockedScans: number; suspiciousActivity: number; verificationRate: number };
}

const analyticsService = new AnalyticsService();

// ═══════════════════════════════════════════════════════════
// Empty State Component
// ═══════════════════════════════════════════════════════════
const EmptyState = ({ message = 'No data available for this period', icon: Icon = Database }: { message?: string; icon?: any }) => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
    <Icon className="h-12 w-12 mb-3 opacity-50" />
    <p className="text-sm">{message}</p>
  </div>
);

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════
const AnalyticsPage: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyticsService.getAnalytics(timeRange);
      if (result.success && result.data) {
        setAnalyticsData(result.data);
      } else {
        setError(result.error || 'Failed to load analytics');
        setAnalyticsData(null);
      }
    } catch {
      setError('Unexpected error loading analytics');
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  // ─── Metric Card ───────────────────────────────────────
  const MetricCard = ({ title, value, change, icon, suffix = '', color = 'blue' }: {
    title: string; value: any; change: number; icon: React.ReactNode;
    suffix?: string; color?: 'blue' | 'green' | 'purple' | 'orange';
  }) => {
    const pos = change >= 0;
    const fv = Number(value || 0).toLocaleString();
    const cc: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-600', green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600', orange: 'bg-orange-100 text-orange-600'
    };
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-gray-500 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{fv}{suffix}</p>
            <div className="flex items-center mt-2">
              <div className={`flex items-center text-sm ${pos ? 'text-green-600' : 'text-red-600'}`}>
                {pos ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}{Math.abs(change)}%
              </div>
              <span className="text-sm text-gray-500 ml-2">vs last period</span>
            </div>
          </div>
          <div className={`p-3 rounded-full ${cc[color]}`}>{icon}</div>
        </div>
      </div>
    );
  };

  // ─── Simple Bar Chart ──────────────────────────────────
  const SimpleChart = ({ data, dataKey, height = 120, color = '#3b82f6' }: {
    data: any[]; dataKey: string; height?: number; color?: string;
  }) => {
    if (!data || data.length === 0) return <EmptyState message="No trend data for this period" icon={BarChart3} />;
    const vals = data.map(d => d[dataKey] || 0);
    const max = Math.max(...vals), min = Math.min(...vals), range = max - min || 1;
    return (
      <div className="relative" style={{ height }}>
        <div className="absolute inset-0 flex items-end justify-between px-2">
          {data.map((item, i) => {
            const v = item[dataKey] || 0, h = ((v - min) / range) * 100;
            return (
              <div key={i} className="flex flex-col items-center flex-1 mx-1">
                <div className="relative w-full">
                  <div className="rounded-t transition-all duration-300 hover:opacity-80 cursor-pointer"
                    style={{ height: `${Math.max(h, 5)}%`, backgroundColor: color, minHeight: '4px' }}
                    title={`${item.date || item.hour || ''}: ${v.toLocaleString()}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Loading ───────────────────────────────────────────
  if (loading) return (
    <div className="p-8">
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    </div>
  );

  // ─── Error (no data at all) ────────────────────────────
  if (error && !analyticsData) return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Comprehensive insights into your product authentication performance</p>
        </div>
        <div className="flex items-center space-x-4">
          <button onClick={loadAnalytics} className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" />Retry
          </button>
        </div>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-800 font-medium text-lg">Unable to Load Analytics</p>
        <p className="text-red-600 text-sm mt-2">{error}</p>
        <button onClick={loadAnalytics} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
          Try Again
        </button>
      </div>
    </div>
  );

  // ─── Main Render ───────────────────────────────────────
  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Comprehensive insights into your product authentication performance</p>
        </div>
        <div className="flex items-center space-x-4">
          <button onClick={loadAnalytics} disabled={loading}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500">
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Download className="h-4 w-4 mr-2" />Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'trends', name: 'Trends', icon: TrendingUp },
              { id: 'locations', name: 'Locations', icon: MapPin },
              { id: 'devices', name: 'Devices', icon: Smartphone },
              { id: 'security', name: 'Security', icon: Shield }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                <tab.icon className="h-4 w-4 mr-2" />{tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════ */}
      {/* Overview Tab                                        */}
      {/* ════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && analyticsData && (
        <div className="space-y-8">
          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <MetricCard title="Total Scans" value={analyticsData.overview.totalScans} change={analyticsData.overview.scanGrowth} icon={<BarChart3 className="h-6 w-6" />} color="blue" />
            <MetricCard title="Unique Users" value={analyticsData.overview.uniqueUsers} change={analyticsData.overview.userGrowth} icon={<Users className="h-6 w-6" />} color="green" />
            <MetricCard title="Conversion Rate" value={analyticsData.overview.conversionRate} change={analyticsData.overview.conversionGrowth} suffix="%" color="purple" icon={<Target className="h-6 w-6" />} />
            <MetricCard title="Daily Average" value={analyticsData.overview.dailyAverage} change={analyticsData.overview.dailyGrowth} icon={<Activity className="h-6 w-6" />} color="orange" />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan Trends</h3>
              <SimpleChart data={analyticsData.scanTrends} dataKey="scans" color="#3b82f6" />
              {analyticsData.scanTrends.length > 0 && (
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>{analyticsData.scanTrends[0]?.date}</span>
                  <span>{analyticsData.scanTrends[analyticsData.scanTrends.length - 1]?.date}</span>
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">User Trends</h3>
              <SimpleChart data={analyticsData.scanTrends} dataKey="users" color="#10b981" />
              {analyticsData.scanTrends.length > 0 && (
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>Unique Users: {analyticsData.overview.uniqueUsers.toLocaleString()}</span>
                  <span className={`flex items-center ${analyticsData.overview.userGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analyticsData.overview.userGrowth >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                    {Math.abs(analyticsData.overview.userGrowth)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Top Products */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Products</h3>
            {analyticsData.topProducts.length === 0 ? (
              <EmptyState message="No product scan data for this period" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scans</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analyticsData.topProducts.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white text-xs font-bold mr-3">
                              {p.name?.charAt(0)}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">{p.scans.toLocaleString()}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center text-sm font-medium ${p.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {p.growth >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                            {Math.abs(p.growth)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════ */}
      {/* Trends Tab                                          */}
      {/* ════════════════════════════════════════════════════ */}
      {activeTab === 'trends' && analyticsData && (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Hourly Activity Pattern</h3>
          <SimpleChart data={analyticsData.hourlyPattern} dataKey="scans" height={200} color="#8b5cf6" />
          {analyticsData.hourlyPattern.some(h => h.scans > 0) ? (
            <div className="flex justify-between text-sm text-gray-500 mt-4">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>12 PM</span>
              <span>6 PM</span>
              <span>11 PM</span>
            </div>
          ) : (
            <p className="text-sm text-gray-400 mt-4 text-center">No hourly data available for this period</p>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════ */}
      {/* Locations Tab                                       */}
      {/* ════════════════════════════════════════════════════ */}
      {activeTab === 'locations' && analyticsData && (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Locations</h3>
          {analyticsData.locationData.length === 0 ? (
            <EmptyState message="No location data available. Scans may not have GPS data." icon={MapPin} />
          ) : (
            <div className="space-y-4">
              {analyticsData.locationData.map((loc, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{i + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{loc.city}</p>
                      <p className="text-xs text-gray-500">{loc.scans.toLocaleString()} scans ({loc.percentage}%)</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${loc.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {loc.growth >= 0 ? '+' : ''}{loc.growth}%
                    </span>
                    {loc.growth >= 0 ? <TrendingUp className="h-4 w-4 text-green-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════ */}
      {/* Devices Tab                                         */}
      {/* ════════════════════════════════════════════════════ */}
      {activeTab === 'devices' && analyticsData && (
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Device Breakdown</h3>
          {analyticsData.deviceBreakdown.length === 0 ? (
            <EmptyState message="No device data available for this period" icon={Smartphone} />
          ) : (
            <div className="space-y-4">
              {analyticsData.deviceBreakdown.map((d, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">{d.device}</span>
                    <span className="text-sm text-gray-500">{d.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${d.percentage}%` }} />
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{d.scans.toLocaleString()} scans</span>
                    <span className={d.growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {d.growth >= 0 ? '+' : ''}{d.growth}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════ */}
      {/* Security Tab                                        */}
      {/* ════════════════════════════════════════════════════ */}
      {activeTab === 'security' && analyticsData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-red-50 rounded-xl p-6 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Fraud Alerts</p>
                <p className="text-2xl font-bold text-red-900">{analyticsData.securityMetrics.fraudAlerts}</p>
                <p className="text-xs text-red-700 mt-1">
                  {analyticsData.securityMetrics.fraudAlerts > 0 ? 'Requires attention' : 'No active alerts'}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Verification Rate</p>
                <p className="text-2xl font-bold text-green-900">{analyticsData.securityMetrics.verificationRate}%</p>
                <p className="text-xs text-green-700 mt-1">
                  {analyticsData.securityMetrics.verificationRate >= 95 ? 'Excellent performance' :
                   analyticsData.securityMetrics.verificationRate >= 80 ? 'Good performance' : 'Needs improvement'}
                </p>
              </div>
              <Shield className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Blocked Scans</p>
                <p className="text-2xl font-bold text-yellow-900">{analyticsData.securityMetrics.blockedScans}</p>
                <p className="text-xs text-yellow-700 mt-1">Scans blocked by fraud detection</p>
              </div>
              <Shield className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800">Suspicious Activity</p>
                <p className="text-2xl font-bold text-orange-900">{analyticsData.securityMetrics.suspiciousActivity}</p>
                <p className="text-xs text-orange-700 mt-1">Medium to critical severity</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;